/* rc-911-clock.js — 9/11 Remembrance 2026: the single schedule clock.
 *
 * Loaded (render-blocking, ~3 KB) from snippets/rc-911-head.liquid on the
 * homepage only, after the snippet has set window.RC911_CFG. Stamps the
 * campaign state on <html> before the header paints so the black header and
 * the hero never flash their normal state, then keeps the state current:
 * next New York midnight timer, a light check while visible, pageshow,
 * visibilitychange, and a same-origin server-clock correction.
 *
 * Exposes window.RC911 for sections/rc-911-remembrance.liquid. No other code
 * computes the campaign date. The visitor's local day of month is never used:
 * every date is derived through Intl.DateTimeFormat in the campaign time zone.
 */
(function (win, doc) {
  'use strict';
  var cfg = win.RC911_CFG || {};
  var TZ = cfg.timezone || 'America/New_York';
  var DAY = 86400000;
  var fmt;
  try {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: TZ, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  } catch (e) {
    /* Unknown time zone or no Intl support: leave the normal homepage. */
    win.RC911 = { state: function () { return { mode: 'off', date: null, reason: 'no-intl' }; }, subscribe: function () {}, now: Date.now, unsupported: true };
    return;
  }

  /* --- zoned date helpers ------------------------------------------------ */
  function partsAt(ms) {
    var o = {};
    fmt.formatToParts(new Date(ms)).forEach(function (p) { o[p.type] = p.value; });
    var h = parseInt(o.hour, 10); if (h === 24) h = 0;
    return { key: o.year + '-' + o.month + '-' + o.day, y: +o.year, mo: +o.month, d: +o.day, h: h, mi: +o.minute, s: +o.second };
  }
  /* UTC instant for a wall-clock time in TZ (two-pass, DST-safe). */
  function zonedToUtc(y, mo, d, h, mi, s) {
    var guess = Date.UTC(y, mo - 1, d, h || 0, mi || 0, s || 0);
    for (var i = 0; i < 2; i++) {
      var p = partsAt(guess);
      var asUtc = Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s);
      guess += Date.UTC(y, mo - 1, d, h || 0, mi || 0, s || 0) - asUtc;
    }
    return guess;
  }
  function parseKey(key) {
    var m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?)?$/.exec(key || '');
    if (!m) return null;
    return { y: +m[1], mo: +m[2], d: +m[3], h: m[4] != null ? +m[4] : null, mi: +m[5] || 0, s: +m[6] || 0 };
  }

  /* --- the clock ------------------------------------------------------- */
  var serverOffset = 0;            /* ms to add to Date.now() (server correction) */
  var override = null;             /* { frozen: ms } or { base: ms, since: ms } */
  function now() {
    if (override) return override.frozen != null ? override.frozen : override.base + (Date.now() - override.since);
    return Date.now() + serverOffset;
  }

  /* --- schedule -------------------------------------------------------- */
  function compute() {
    if (!cfg.enabled) return { mode: 'off', date: null, reason: 'disabled' };
    if (!cfg.start || !cfg.end) return { mode: 'off', date: null, reason: 'no-window' };
    var key = partsAt(now()).key;
    if (key < cfg.start || key >= cfg.end) return { mode: 'off', date: key, reason: 'outside' };
    return { mode: key === cfg.video ? 'video' : 'text', date: key, reason: 'active' };
  }
  function nextBoundary() {
    var p = partsAt(now());
    return zonedToUtc(p.y, p.mo, p.d + 1, 0, 0, 0);
  }

  /* --- html stamping + subscribers ------------------------------------- */
  var root = doc.documentElement;
  var listeners = [];
  var current = null;
  function apply(force) {
    var st = compute();
    var changed = !current || current.mode !== st.mode || current.date !== st.date;
    if (!changed && !force) return st;
    current = st;
    if (st.mode === 'off') {
      root.removeAttribute('data-rc911-mode');
      root.removeAttribute('data-rc911-date');
      root.removeAttribute('data-rc911-over');
    } else {
      root.setAttribute('data-rc911-mode', st.mode);
      root.setAttribute('data-rc911-date', st.date);
      if (!root.hasAttribute('data-rc911-over')) root.setAttribute('data-rc911-over', '1');
    }
    for (var i = 0; i < listeners.length; i++) { try { listeners[i](st); } catch (e) { /* keep others alive */ } }
    return st;
  }

  /* --- timers ---------------------------------------------------------- */
  var boundaryTimer = null, tick = null;
  function scheduleBoundary() {
    if (boundaryTimer) clearTimeout(boundaryTimer);
    if (override && override.frozen != null) return; /* frozen preview: no rollover */
    var wait = nextBoundary() - now() + 250;
    if (wait < 250) wait = 250;
    if (wait > 2147000000) wait = 2147000000;
    boundaryTimer = setTimeout(function () { apply(); scheduleBoundary(); }, wait);
  }
  function startTick() {
    if (tick) clearInterval(tick);
    tick = setInterval(function () { if (!doc.hidden) { apply(); } }, 30000);
  }
  function refresh() { apply(); scheduleBoundary(); }

  /* --- preview overrides (never on the live theme) --------------------- */
  function setOverride(spec) {
    override = null;
    if (spec && spec !== 'off') {
      var k = parseKey(spec);
      if (k) {
        if (k.h == null) override = { frozen: zonedToUtc(k.y, k.mo, k.d, 12, 0, 0) };
        else override = { base: zonedToUtc(k.y, k.mo, k.d, k.h, k.mi, k.s), since: Date.now() };
      }
    }
    refresh();
    return override ? spec : null;
  }
  var previewSpec = null;
  if (cfg.allowPreview) {
    try {
      var q = /[?&]rc911=([^&#]+)/.exec(win.location.search);
      if (q) previewSpec = decodeURIComponent(q[1]);
      else if (cfg.designMode && cfg.previewSetting && cfg.previewSetting !== 'off') previewSpec = cfg.previewSetting;
    } catch (e) { previewSpec = null; }
  }
  if (previewSpec) setOverride(previewSpec);

  /* --- initial stamp (before body paints) ------------------------------ */
  apply(true);
  /* The server rendered today's day in the shop time zone. If this browser's
     campaign day differs (preview link, or a page cached across midnight), keep
     the day's content undisplayed until the section script has swapped it in,
     so the wrong day never paints. A 4 s safety net shows the server's version
     if that script never runs. */
  var staleTimer = null;
  function settle() { root.removeAttribute('data-rc911-stale'); if (staleTimer) { clearTimeout(staleTimer); staleTimer = null; } }
  if (current && current.mode !== 'off' && cfg.serverDate && (current.date !== cfg.serverDate || current.mode !== cfg.serverMode)) {
    root.setAttribute('data-rc911-stale', '1');
    staleTimer = setTimeout(settle, 4000);
  }

  /* --- lifecycle ------------------------------------------------------- */
  function onReady() {
    scheduleBoundary();
    startTick();
    win.addEventListener('pageshow', function () { refresh(); });
    doc.addEventListener('visibilitychange', function () { if (!doc.hidden) refresh(); });
    /* Server clock: the Date header of a same-origin HEAD request. Only applied
       when the device clock is off by more than a minute. */
    if (!override && win.fetch) {
      try {
        var sent = Date.now();
        win.fetch(win.location.pathname, { method: 'HEAD', cache: 'no-store', credentials: 'same-origin' }).then(function (r) {
          var d = r && r.headers && r.headers.get('date');
          if (!d) return;
          var srv = Date.parse(d);
          if (isNaN(srv)) return;
          var mid = (sent + Date.now()) / 2;
          var off = srv + 500 - mid; /* Date header has 1 s resolution */
          if (Math.abs(off) > 60000) { serverOffset = off; root.setAttribute('data-rc911-clock', 'server'); refresh(); }
        }).catch(function () {});
      } catch (e) {}
    }
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', onReady); else onReady();

  win.RC911 = {
    cfg: cfg,
    now: now,
    state: function () { return current || compute(); },
    partsAt: partsAt,
    zonedToUtc: zonedToUtc,
    nextBoundary: nextBoundary,
    refresh: refresh,
    subscribe: function (fn) { listeners.push(fn); if (current) { try { fn(current); } catch (e) {} } return function () { var i = listeners.indexOf(fn); if (i >= 0) listeners.splice(i, 1); }; },
    preview: function (spec) { return cfg.allowPreview ? setOverride(spec) : null; },
    previewSpec: previewSpec,
    settle: settle
  };
})(window, document);
